import { z } from 'zod'
import type { ApiClient } from './client'

/**
 * ML models — the legacy /system/models surface (enterprise tier).
 *
 * The model definition is a base64 YAML blob the client never parses;
 * legacy does btoa/atob only. Reads live under /api/, writes under
 * /wrk/api/ — the only PUT/DELETE verbs in the whole legacy API surface.
 *
 * ⚠️ Shapes UNVERIFIED — reconstructed 2026-08-22 from legacy source
 * (src/services/models.js, src/pages/admin/models/). No live capture
 * exists for /api/models/* yet; capture before live-mode writes ship.
 * Known model-shaped facts: rows carry ONLY {model_id, model_name,
 * model_status}; status vocabulary is 'ENABLED' | 'DISABLED'; creation
 * always lands DISABLED; there is no severity/MITRE/schedule/version
 * field anywhere (docs/detections-requirements.md flags technique-tagging
 * as a backend ask).
 */

export const mlModel = z
  .object({
    model_id: z.union([z.string(), z.number()]).transform(String),
    model_name: z.string().default(''),
    /** 'ENABLED' | 'DISABLED'. Kept permissive. */
    model_status: z.string().default(''),
  })
  .passthrough()

export const mlModelsResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.array(mlModel).default([]),
})

/** details returns the base64 YAML as `data` (a bare string). */
export const mlModelDetailsResponse = z.object({
  status: z.string().optional(),
  data: z.string().default(''),
})

const mutationResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
})

export type MlModel = z.output<typeof mlModel>

export function isModelEnabled(m: Pick<MlModel, 'model_status'>): boolean {
  return m.model_status.toUpperCase() === 'ENABLED'
}

/** Decode the definition blob; junk falls back to the raw string. */
export function decodeModelYaml(b64: string): string {
  try {
    return atob(b64)
  } catch {
    return b64
  }
}

/* --------------------------------------------------------------------- api */

export interface ModelsScope {
  clusterId: string
  scopeId: string
}

export interface ModelsApi {
  list: (q: ModelsScope & { pageNo?: number; pageSize?: number }) => Promise<MlModel[]>
  /** The decoded YAML definition for one model. */
  definition: (q: ModelsScope & { modelId: string }) => Promise<string>
  enable: (q: ModelsScope & { modelId: string }) => Promise<void>
  disable: (q: ModelsScope & { modelId: string }) => Promise<void>
  remove: (q: ModelsScope & { modelId: string }) => Promise<void>
  /** Create; the backend forces DISABLED on creation (legacy contract). */
  add: (q: ModelsScope & { yaml: string }) => Promise<void>
  edit: (q: ModelsScope & { yaml: string }) => Promise<void>
}

export function createModelsApi(client: ApiClient): ModelsApi {
  return {
    list: async ({ clusterId, scopeId, pageNo = 1, pageSize = 1000 }) => {
      const res = await client.request('POST', `/${clusterId}/api/models/list`, {
        body: { scope_id: scopeId, pageno: pageNo, pagesize: pageSize },
        schema: mlModelsResponse,
      })
      return res.data
    },

    definition: async ({ clusterId, scopeId, modelId }) => {
      const res = await client.request('POST', `/${clusterId}/api/models/list/details`, {
        body: { scope_id: scopeId, model_id: modelId },
        schema: mlModelDetailsResponse,
      })
      return decodeModelYaml(res.data)
    },

    enable: async ({ clusterId, scopeId, modelId }) => {
      await client.request('PUT', `/${clusterId}/wrk/api/models/enable`, {
        body: { scope_id: scopeId, model_id: modelId },
        schema: mutationResponse,
      })
    },

    disable: async ({ clusterId, scopeId, modelId }) => {
      await client.request('PUT', `/${clusterId}/wrk/api/models/disable`, {
        body: { scope_id: scopeId, model_id: modelId },
        schema: mutationResponse,
      })
    },

    remove: async ({ clusterId, scopeId, modelId }) => {
      await client.request('DELETE', `/${clusterId}/wrk/api/models/delete`, {
        body: { scope_id: scopeId, model_id: modelId },
        schema: mutationResponse,
      })
    },

    add: async ({ clusterId, scopeId, yaml }) => {
      await client.request('POST', `/${clusterId}/api/models/add`, {
        body: { scope_id: scopeId, data: btoa(yaml), status: 'DISABLED' },
        schema: mutationResponse,
      })
    },

    edit: async ({ clusterId, scopeId, yaml }) => {
      await client.request('PUT', `/${clusterId}/wrk/api/models/edit`, {
        body: { scope_id: scopeId, data: btoa(yaml) },
        schema: mutationResponse,
      })
    },
  }
}
